import { TableColumn, TableColumnOptionsFilter, TableFilter, TableFilterValue, TableGroupRowData, TableSortValue } from './types';

export const setUrlParameters = (() => {
  let delayId: NodeJS.Timeout | undefined;

  return (parameters: Record<string, string>) => {
    const searchParams = new URLSearchParams(window.location.search);

    Object.keys(parameters).forEach((parameter) => {
      searchParams.set(parameter, parameters[parameter]);
    });
    if (delayId) {
      clearTimeout(delayId);
    }
    delayId = setTimeout(() => {
      // eslint-disable-next-line no-restricted-globals
      history.pushState(
        null,
        '',
        `${window.location.pathname}?${searchParams.toString()}`,
      );
    }, 100);
  };
})();

export function stringifySort<I = any>({ name, direction }: TableSortValue<I>) {
  return `${direction === 'asc' ? '+' : '-' }${name.toString()}`;
}

export function filterMarshalling<I = any>(filters: TableFilter<I>[]): Record<string, string | string[]> {
  return filters.reduce((map, filter) => {
    if (!filter.value || filter.value.length === 0) {return map;}
    map[filter.name.toString()] = filter.value;
    // TODO: add support for scope  
    return map;
  }, {});
}

export function filterUnmarshalling<I = any>(filter: Record<string, string | string[]>, columns: TableColumn<I>[]): TableFilter<I>[] {
  const filterArr: TableFilter<I>[] = [];

  columns.forEach((column) => {
    if (column.options?.filter) {
      const filterOptions = column.options.filter as TableColumnOptionsFilter;
      const name: keyof I = filterOptions.name as keyof I || column.name;
      const type = filterOptions.type || 'text';
      
      const isMultiple = filterOptions.type === 'multi-select' || 
        filterOptions.type === 'date-range';
      const hasUrlValue = Object.prototype.hasOwnProperty.call(filter, name.toString());
      const value = hasUrlValue
        ? filter[name.toString()]
        : (filterOptions.defaultValue !== undefined
          ? filterOptions.defaultValue
          : (isMultiple ? [] : ''));

      filterArr.push({
        name,
        type,
        label: filterOptions.label || column.label,
        value,
        scope: 'include', // TODO: implement scope
        source: filterOptions.sourceName || '',
        onOptionsFetch: filterOptions.onOptionsFetch,
        isApplied: filterOptions.isApplied || false
      });
    }
  });
  return filterArr;
}

export function filterMapping<I = any>(filters: TableFilter<I>[], columns: TableColumn<I>[]): Record<string, string | string[]> {
  return filters.reduce((map, filter) => {
    if (!filter.value || filter.value.length === 0) {return map;}
    const column = columns.find((column) => (column.options?.filter as TableColumnOptionsFilter)?.name === filter.name);
    if ((column?.options?.filter as TableColumnOptionsFilter)?.onApply) {
      map[filter.name.toString()] = (column.options?.filter as TableColumnOptionsFilter).onApply(filter.value);
      return map;
    }
    switch(filter.type) {
      case 'text': 
        map[filter.name.toString()] = filter.value as string;
        break;
      case 'select': 
        map[filter.name.toString()] = (filter.value as EntityBase).id;
        break;
      case 'autocomplete': 
        map[filter.name.toString()] = (filter.value as EntityBase).id;
        break;
      case 'multi-select': 
        map[filter.name.toString()] = (filter.value as EntityBase[]).map(({ id }) => id);
        break;
      case 'date':
        map[filter.name.toString()] = filter.value as string;
        break;
      case 'date-time':
        map[filter.name.toString()] = filter.value as string;
        break;
      case 'date-range':
        map[filter.name.toString() + '_b'] = filter.value[0];
        map[filter.name.toString() + '_e'] = filter.value[1];
        break;
      case 'boolean':
        map[filter.name.toString()] = filter.value as string;
        break;
      case 'outlet':
        map[filter.name.toString()] = (filter.value as EntityBase).id;
        break;
      case 'autocomplete-paginated':
        map[filter.name.toString()] = (filter.value as EntityBase).id;
        break;
    }
    return map;
  }, {});
}
export function isGroupRow<I extends EntityBase = EntityBase>(row: any): row is TableGroupRowData<I> {
  return !!row.group && row._isGroup;
}